import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Brain, Star, Heart, CheckCircle,
  ChevronRight, Award, Sparkles, Loader2, X, Trophy, Lock, BookOpen, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const CoursePage = ({ user }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({});
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [moduleIdx, setModuleIdx] = useState(0);
  const [pageIdx, setPageIdx] = useState(0);
  const [pageData, setPageData] = useState({});
  const [quizHearts, setQuizHearts] = useState(3);
  const [quizCurrent, setQuizCurrent] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [showOverview, setShowOverview] = useState(true);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await fetch(`${API}/api/courses/${courseId}`, { headers: hdr() });
        if (res.ok) {
          const d = await res.json();
          setCourse(d.course);
          setProgress(d.progress);
          setResponses(d.responses || {});
          if (d.progress.current_module > 0) {
            setModuleIdx(d.progress.current_module - 1);
            setShowOverview(false);
          }
          if (d.progress.current_page > 0) setPageIdx(d.progress.current_page);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchCourse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const currentModule = course?.modules?.[moduleIdx];
  const currentPage = currentModule?.pages?.[pageIdx];
  const totalPagesInModule = currentModule?.pages?.length || 0;
  const totalModules = course?.modules?.length || 0;
  const isFirstPage = moduleIdx === 0 && pageIdx === 0;
  const isLastPageInModule = pageIdx >= totalPagesInModule - 1;
  const isLastModule = moduleIdx >= totalModules - 1;

  const saveResponse = useCallback(async (pageId, data, xp = 0) => {
    setResponses(prev => ({ ...prev, [pageId]: { ...prev[pageId], ...data } }));
    try {
      await fetch(`${API}/api/courses/${courseId}/save-response`, {
        method: 'POST', headers: hdr(), body: JSON.stringify({ page_id: pageId, data, xp })
      });
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const goNext = async () => {
    // Save progress
    try {
      await fetch(`${API}/api/courses/${courseId}/complete-page`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ module_number: currentModule?.number || 0, page_index: pageIdx + 1 })
      });
    } catch (e) { console.error(e); }

    if (isLastPageInModule) {
      if (isLastModule) {
        await fetch(`${API}/api/courses/${courseId}/complete`, { method: 'POST', headers: hdr() });
        toast.success('Course completed! +50 XP');
        navigate(-1);
      } else {
        setModuleIdx(prev => prev + 1);
        setPageIdx(0);
        setQuizHearts(3); setQuizCurrent(0); setQuizDone(false);
      }
    } else {
      setPageIdx(prev => prev + 1);
    }
    setPageData({});
  };

  const goBack = () => {
    if (pageIdx > 0) setPageIdx(prev => prev - 1);
    else if (moduleIdx > 0) {
      setModuleIdx(prev => prev - 1);
      const prevModule = course.modules[moduleIdx - 1];
      setPageIdx((prevModule?.pages?.length || 1) - 1);
    }
    setPageData({});
  };

  const handleQuizAnswer = async (questionIdx, answerIdx) => {
    const q = currentPage.questions[questionIdx];
    const correct = answerIdx === q.correct;
    if (!correct) setQuizHearts(prev => Math.max(0, prev - 1));
    
    setPageData(prev => ({ ...prev, [`q${questionIdx}`]: { selected: answerIdx, correct } }));
    
    if (questionIdx >= currentPage.questions.length - 1) {
      setQuizDone(true);
      const finalHearts = correct ? quizHearts : Math.max(0, quizHearts - 1);
      await fetch(`${API}/api/courses/${courseId}/complete-quiz`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({
          score: currentPage.questions.length, hearts_remaining: finalHearts,
          badge_id: currentPage.badge?.id, module_number: currentModule?.number
        })
      });
      toast.success(`Badge earned: ${currentPage.badge?.name}!`);
    } else {
      setTimeout(() => setQuizCurrent(prev => prev + 1), 1500);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
    </div>
  );

  if (!course) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <p className="text-gray-500">Course not found</p>
    </div>
  );

  const name = user?.name?.split(' ')[0] || 'there';
  const aiMessage = currentPage?.ai_guide?.replace('{{name}}', name);
  const savedData = currentPage ? (responses[currentPage.id] || {}) : {};

  // Course Overview Screen
  if (showOverview) {
    const completedModules = progress.current_module || 0;
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="pb-12 pt-4 px-4">
          <div className="max-w-lg mx-auto">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 mb-4" data-testid="overview-back-btn">
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <p className="text-orange-400 text-xs font-bold uppercase tracking-wider mb-1">{course.category}</p>
            <h1 className="text-3xl font-black text-white mb-2">{course.title}</h1>
            <p className="text-sm text-white/60 leading-relaxed">{course.subtitle}</p>
            <div className="flex items-center gap-4 mt-4 text-xs text-white/40">
              <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" />{course.total_modules} modules</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" />{course.total_xp} XP</span>
              <span>{course.estimated_minutes} min</span>
            </div>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 -mt-6 space-y-3 pb-8">
          {course.modules.map((m, i) => {
            const isCompleted = i < completedModules;
            const isCurrent = i === completedModules;
            const isLocked = i > completedModules;
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                onClick={() => { if (!isLocked) { setModuleIdx(i); setPageIdx(0); setShowOverview(false); }}}
                className={`p-4 rounded-2xl border transition-all ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                style={{ backgroundColor: isCompleted ? '#ECFDF5' : 'white', borderColor: isCurrent ? '#E86C3D' : isCompleted ? '#A7F3D0' : '#E5E7EB' }}
                data-testid={`module-${i}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black ${isCompleted ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : m.number}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">{m.title}</p>
                    <p className="text-[10px] text-gray-400">{m.pages?.length || 0} pages · {m.time_minutes} min</p>
                  </div>
                  {isCurrent && <ChevronRight className="w-5 h-5 text-orange-500" />}
                  {isLocked && <Lock className="w-4 h-4 text-gray-300" />}
                </div>
              </motion.div>
            );
          })}
          <Button onClick={() => { setModuleIdx(completedModules < totalModules ? completedModules : 0); setPageIdx(0); setShowOverview(false); }}
            className="w-full py-6 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-base shadow-lg shadow-orange-500/20"
            data-testid="start-course-btn">
            {completedModules > 0 ? 'Continue Learning' : 'Start Course'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (!currentPage) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <p className="text-gray-500">Page not found</p>
    </div>
  );

  const isHookPage = currentPage.type === 'hook';
  const isActivityPage = currentPage.type === 'activity';
  const isCelebration = currentPage.type === 'celebration';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: isHookPage ? '#0A0E1A' : isCelebration ? '#0A0E1A' : '#F5F3EF' }}>
      {/* Top bar */}
      <div className={`px-4 py-3 flex items-center gap-3 ${isHookPage || isCelebration ? 'border-b border-white/10' : 'border-b border-gray-200 bg-white'}`}>
        <button onClick={() => setShowOverview(true)} className={`p-2 rounded-xl transition-all ${isHookPage || isCelebration ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`} data-testid="back-btn">
          <ArrowLeft className={`w-5 h-5 ${isHookPage || isCelebration ? 'text-white/60' : 'text-gray-500'}`} />
        </button>
        <div className="flex-1">
          <p className={`text-[10px] font-bold uppercase tracking-wider ${isHookPage || isCelebration ? 'text-white/40' : 'text-gray-400'}`}>
            Module {currentModule.number}: {currentModule.title}
          </p>
          <Progress value={((pageIdx + 1) / totalPagesInModule) * 100} className={`h-1.5 mt-1 ${isHookPage || isCelebration ? 'bg-white/10' : 'bg-gray-200'}`} />
        </div>
        <div className="flex items-center gap-1 text-amber-500">
          <Star className="w-3.5 h-3.5" fill="currentColor" />
          <span className="text-xs font-bold">{progress.xp_earned || 0}</span>
        </div>
      </div>

      {/* Page content */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div key={`${moduleIdx}-${pageIdx}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            className="max-w-lg mx-auto px-5 py-6 space-y-5">

            {/* AI Guide bubble */}
            {aiMessage && (
              <div className="flex gap-3">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isHookPage || isCelebration ? 'bg-orange-500/20' : 'bg-orange-50'}`}>
                  <Brain className={`w-4 h-4 ${isHookPage || isCelebration ? 'text-orange-400' : 'text-orange-500'}`} />
                </div>
                <div className={`p-3 rounded-2xl rounded-tl-sm ${isHookPage || isCelebration ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-100'}`}>
                  <p className={`text-sm ${isHookPage || isCelebration ? 'text-orange-200' : 'text-orange-800'}`}>{aiMessage}</p>
                </div>
              </div>
            )}

            {/* CONTENT / HOOK page */}
            {(currentPage.type === 'content' || currentPage.type === 'hook') && (
              <div className="space-y-4">
                <div className="space-y-2">
                  {currentPage.content.split('\n').map((line, i) => (
                    <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
                      className={`${line.trim() === '' ? 'h-3' : isHookPage ? 'text-white text-lg font-semibold leading-relaxed' : 'text-gray-800 text-base leading-relaxed'}`}>
                      {line}
                    </motion.p>
                  ))}
                </div>
                {currentPage.items && (
                  <div className="space-y-2 mt-4">
                    {currentPage.items.map((item, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.1 }}
                        className={`flex items-start gap-3 p-3 rounded-xl ${isHookPage ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-100 shadow-sm'}`}>
                        <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isHookPage ? 'text-emerald-400' : 'text-emerald-500'}`} />
                        <p className={`text-sm ${isHookPage ? 'text-white/80' : 'text-gray-700'}`}>{item}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
                {currentPage.footer && <p className={`text-sm italic ${isHookPage ? 'text-white/40' : 'text-gray-400'}`}>{currentPage.footer}</p>}
              </div>
            )}

            {/* MOOD SELECTOR activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'mood_selector' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="grid grid-cols-2 gap-3">
                  {currentPage.options.map((opt, i) => (
                    <button key={i} onClick={() => { setPageData({...pageData, mood: opt.value}); saveResponse(currentPage.id, { ei_baseline_mood: opt.value }, 0); }}
                      className={`p-4 rounded-2xl text-left transition-all ${pageData.mood === opt.value ? 'bg-orange-50 ring-2 ring-orange-400 scale-[1.02] shadow-md' : 'bg-white hover:bg-gray-50 border border-gray-100 shadow-sm'}`}>
                      <span className="text-2xl block mb-1">{opt.emoji}</span>
                      <span className="text-xs text-gray-700 font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
                {pageData.mood && currentPage.followup_prompt && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <p className="text-sm text-gray-500 mb-2">{currentPage.followup_prompt}</p>
                    <input className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-sm"
                      placeholder="One word..."
                      onChange={e => { setPageData({...pageData, word: e.target.value}); saveResponse(currentPage.id, { ei_baseline_mood: pageData.mood, ei_baseline_word: e.target.value }, currentPage.xp || 0); }} />
                  </motion.div>
                )}
              </div>
            )}

            {/* DUAL SLIDER activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'dual_slider' && (
              <div className="space-y-5">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                {currentPage.sliders.map((slider, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm space-y-2">
                    <p className="text-sm text-gray-700 font-medium">{slider.label}</p>
                    <input type="range" min={slider.min} max={slider.max} value={pageData[slider.save_key] || 5}
                      onChange={e => { const val = parseInt(e.target.value); setPageData({...pageData, [slider.save_key]: val}); saveResponse(currentPage.id, { [slider.save_key]: val }, i === currentPage.sliders.length - 1 ? (currentPage.xp || 0) : 0); }}
                      className="w-full accent-orange-500" />
                    <div className="flex justify-between text-[10px] text-gray-400">
                      <span>{slider.anchors?.['1'] || slider.min}</span>
                      <span className="text-orange-500 font-bold text-sm">{pageData[slider.save_key] || 5}/{slider.max}</span>
                      <span>{slider.anchors?.['10'] || slider.max}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* FLIP CARDS activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'flip_cards' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="grid grid-cols-2 gap-3">
                  {currentPage.cards.map((card, i) => (
                    <button key={i} onClick={() => { setPageData({...pageData, [`flip_${i}`]: !pageData[`flip_${i}`]}); if (!pageData.flipped_count) saveResponse(currentPage.id, { ei_pillars_explored: true }, currentPage.xp || 0); }}
                      className={`relative h-32 rounded-2xl overflow-hidden transition-all shadow-sm hover:shadow-md ${pageData[`flip_${i}`] ? 'ring-2 ring-orange-400' : ''}`}>
                      <div className="w-full h-full flex items-center justify-center text-center p-4"
                        style={{ backgroundColor: pageData[`flip_${i}`] ? '#FFF7ED' : 'white', border: `1px solid ${pageData[`flip_${i}`] ? '#FDBA74' : '#E5E7EB'}` , borderRadius: '1rem' }}>
                        <p className={`text-sm font-bold ${pageData[`flip_${i}`] ? 'text-orange-700' : 'text-gray-800'}`}>
                          {pageData[`flip_${i}`] ? card.back : card.front}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
                {currentPage.footer && <p className="text-sm text-gray-400">{currentPage.footer}</p>}
              </div>
            )}

            {/* CAROUSEL activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'carousel' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="space-y-3">
                  {currentPage.cards.map((card, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.3 }}
                      className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                      <p className="text-sm text-gray-700">{card}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* DRAG TO CATEGORIZE activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'drag_categorize' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                {currentPage.items.map((item, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white border border-gray-100 shadow-sm space-y-2">
                    <p className="text-sm text-gray-700">{item.text}</p>
                    <div className="flex gap-1 flex-wrap">
                      {currentPage.categories.map(cat => (
                        <button key={cat} onClick={() => {
                          const answers = { ...pageData, [`item_${i}`]: cat };
                          setPageData(answers);
                          const correct = Object.keys(answers).filter(k => k.startsWith('item_')).filter(k => {
                            const idx = parseInt(k.split('_')[1]);
                            return answers[k] === currentPage.items[idx]?.correct;
                          }).length;
                          saveResponse(currentPage.id, { ei_pillar_match_score: correct }, correct === currentPage.items.length ? (currentPage.xp || 0) : 0);
                        }}
                          className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all ${pageData[`item_${i}`] === cat
                            ? (cat === item.correct ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-400' : 'bg-red-100 text-red-700 ring-1 ring-red-400')
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* MULTI SELECT activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'multi_select' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="space-y-2">
                  {currentPage.options.map((opt, i) => {
                    const selected = (pageData.selected || []).includes(i);
                    return (
                      <button key={i} onClick={() => {
                        const sel = pageData.selected || [];
                        const next = selected ? sel.filter(x => x !== i) : [...sel, i];
                        setPageData({...pageData, selected: next});
                        const vals = next.map(idx => currentPage.options[idx]);
                        saveResponse(currentPage.id, { [currentPage.save_keys?.[0] || 'selected']: vals }, next.length > 0 ? (currentPage.xp || 0) : 0);
                      }}
                        className={`w-full p-3 rounded-xl text-left text-sm transition-all flex items-center gap-3 ${selected ? 'bg-orange-50 ring-1 ring-orange-400 text-orange-800' : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-100 shadow-sm'}`}>
                        <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center ${selected ? 'bg-orange-500' : 'bg-gray-100 border border-gray-200'}`}>
                          {selected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SWIPE CARDS activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'swipe_cards' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="space-y-3">
                  {currentPage.cards.map((card, i) => {
                    const answer = pageData[`swipe_${i}`];
                    const isCorrect = answer === card.correct;
                    return (
                      <div key={i} className={`p-4 rounded-2xl border transition-all ${answer ? (isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200') : 'bg-white border-gray-100 shadow-sm'}`}>
                        <p className="text-sm text-gray-700 mb-3">{card.text}</p>
                        {!answer ? (
                          <div className="flex gap-2">
                            <button onClick={() => { const d = {...pageData, [`swipe_${i}`]: 'left'}; setPageData(d); const score = Object.keys(d).filter(k=>k.startsWith('swipe_')).filter(k=>{const idx=parseInt(k.split('_')[1]);return d[k]===currentPage.cards[idx]?.correct;}).length; saveResponse(currentPage.id, {[currentPage.save_keys?.[0]||'swipe_score']:score}, score===currentPage.cards.length?(currentPage.xp||0):0); }}
                              className="px-4 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-600 hover:bg-red-200 transition-all">← LEFT</button>
                            <button onClick={() => { const d = {...pageData, [`swipe_${i}`]: 'right'}; setPageData(d); const score = Object.keys(d).filter(k=>k.startsWith('swipe_')).filter(k=>{const idx=parseInt(k.split('_')[1]);return d[k]===currentPage.cards[idx]?.correct;}).length; saveResponse(currentPage.id, {[currentPage.save_keys?.[0]||'swipe_score']:score}, score===currentPage.cards.length?(currentPage.xp||0):0); }}
                              className="px-4 py-1.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-all">RIGHT →</button>
                          </div>
                        ) : (
                          <p className={`text-xs font-bold ${isCorrect ? 'text-emerald-600' : 'text-red-600'}`}>{isCorrect ? 'Correct!' : `Wrong — answer was ${card.correct}`}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SCENARIO CHOICE activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'scenario_choice' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                {currentPage.prompt && <p className="text-sm text-gray-500">{currentPage.prompt}</p>}
                <div className="space-y-2">
                  {currentPage.options.map((opt, i) => {
                    const answered = pageData.scenario_answer !== undefined;
                    const isCorrect = i === currentPage.correct;
                    const isSelected = pageData.scenario_answer === i;
                    return (
                      <button key={i} disabled={answered} onClick={() => {
                        setPageData({...pageData, scenario_answer: i});
                        saveResponse(currentPage.id, { [currentPage.save_keys?.[0] || 'answer']: i === currentPage.correct }, i === currentPage.correct ? (currentPage.xp || 0) : 0);
                      }}
                        className={`w-full p-3 rounded-xl text-left text-sm transition-all border ${answered ? (isCorrect ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : isSelected ? 'bg-red-50 border-red-300 text-red-800' : 'bg-white border-gray-100 text-gray-400') : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-100 shadow-sm'}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {pageData.scenario_answer !== undefined && currentPage.feedback && (
                  <motion.p initial={{opacity:0}} animate={{opacity:1}} className="text-sm text-orange-800 italic p-3 rounded-xl bg-orange-50 border border-orange-200">{currentPage.feedback}</motion.p>
                )}
              </div>
            )}

            {/* BRANCHING SCENARIO activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'branching_scenario' && (
              <div className="space-y-5">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                {currentPage.scenarios?.map((scenario, si) => (
                  <div key={si} className="space-y-3">
                    <p className="text-sm text-gray-800 font-semibold p-3 rounded-xl bg-white border border-gray-100 shadow-sm">{scenario.situation}</p>
                    <div className="space-y-2 pl-3">
                      {scenario.options.map((opt, oi) => {
                        const chosen = pageData[`branch_${si}`] === oi;
                        return (
                          <button key={oi} onClick={() => { setPageData({...pageData, [`branch_${si}`]: oi}); saveResponse(currentPage.id, {[`scenario_${si}`]: opt.text}, si === (currentPage.scenarios.length-1) ? (currentPage.xp||0) : 0); }}
                            className={`w-full p-3 rounded-xl text-left text-sm transition-all border ${chosen ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-100 hover:bg-gray-50 shadow-sm'}`}>
                            <p className={chosen ? 'text-orange-800' : 'text-gray-700'}>{opt.text}</p>
                            {chosen && <p className="text-xs text-orange-600 mt-1 italic">→ {opt.result}</p>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* BEFORE/AFTER activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'before_after' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <p className="text-sm text-gray-500 italic">{currentPage.scenario}</p>
                <div className="flex gap-2 mb-3">
                  <button onClick={() => setPageData({...pageData, view: 'before'})} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${pageData.view !== 'after' ? 'bg-red-100 text-red-700 ring-1 ring-red-300' : 'bg-gray-100 text-gray-400'}`}>Inner Critic</button>
                  <button onClick={() => setPageData({...pageData, view: 'after'})} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${pageData.view === 'after' ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300' : 'bg-gray-100 text-gray-400'}`}>Inner Coach</button>
                </div>
                <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                  <p className="text-sm text-gray-700 leading-relaxed">{pageData.view === 'after' ? currentPage.after : currentPage.before}</p>
                </div>
                {currentPage.followup && (
                  <div className="space-y-2 mt-3">
                    <p className="text-sm text-gray-500">{currentPage.followup}</p>
                    <div className="flex gap-2">
                      {currentPage.options.map((opt, i) => (
                        <button key={i} onClick={() => { setPageData({...pageData, followup_choice: i}); saveResponse(currentPage.id, {[currentPage.save_keys?.[0] || 'choice']: opt}, currentPage.xp || 0); }}
                          className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${pageData.followup_choice === i ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{opt}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* GUIDED STEPS activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'guided_steps' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="space-y-3">
                  {currentPage.steps?.map((step, i) => {
                    const done = (pageData.steps_done || 0) > i;
                    const current = (pageData.steps_done || 0) === i;
                    return (
                      <button key={i} onClick={() => { if (current) { const next = i + 1; setPageData({...pageData, steps_done: next}); if (next >= currentPage.steps.length) saveResponse(currentPage.id, {[currentPage.save_keys?.[0] || 'done']: true}, currentPage.xp || 0); }}}
                        className={`w-full p-4 rounded-xl text-left text-sm transition-all border flex items-center gap-3 ${done ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : current ? 'bg-orange-50 border-orange-300 text-orange-700 shadow-md' : 'bg-white border-gray-100 text-gray-400'}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${done ? 'bg-emerald-500 text-white' : current ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-400'}`}>{done ? '✓' : i + 1}</div>
                        {step}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ROLE SELECTOR activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'role_selector' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="flex gap-2 flex-wrap">
                  {currentPage.roles?.map((r, i) => (
                    <button key={i} onClick={() => { setPageData({...pageData, role: i}); saveResponse(currentPage.id, {selected_role: r.role}, currentPage.xp || 0); }}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${pageData.role === i ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300 shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100 shadow-sm'}`}>{r.role}</button>
                  ))}
                </div>
                {pageData.role !== undefined && currentPage.roles?.[pageData.role] && (
                  <motion.div initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <p className="text-xs text-orange-500 font-bold mb-2">{currentPage.roles[pageData.role].role}'s perspective:</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{currentPage.roles[pageData.role].perspective}</p>
                  </motion.div>
                )}
              </div>
            )}

            {/* OPT-IN activity */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'opt_in' && (
              <div className="space-y-4 text-center py-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <div className="flex gap-3 justify-center">
                  {currentPage.options.map((opt, i) => (
                    <button key={i} onClick={() => { setPageData({...pageData, opt_in: i}); saveResponse(currentPage.id, {[currentPage.save_keys?.[0] || 'opt_in']: opt}, currentPage.xp || 0); }}
                      className={`px-6 py-3 rounded-xl text-sm font-bold transition-all ${pageData.opt_in === i ? 'bg-orange-500 text-white scale-105 shadow-lg shadow-orange-500/20' : i === 0 ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{opt}</button>
                  ))}
                </div>
              </div>
            )}

            {/* ACTIVITY-TYPE REFLECTION */}
            {currentPage.type === 'activity' && currentPage.activity_type === 'reflection' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                {currentPage.prompt && <p className="text-sm text-gray-500">{currentPage.prompt}</p>}
                <Textarea
                  value={pageData.reflection || savedData[currentPage.save_keys?.[0]] || ''}
                  onChange={e => setPageData({...pageData, reflection: e.target.value})}
                  onBlur={e => saveResponse(currentPage.id, { [currentPage.save_keys?.[0] || 'reflection']: e.target.value }, currentPage.xp || 0)}
                  placeholder="Be real..." rows={4}
                  className="bg-white border-gray-200 text-gray-800 placeholder:text-gray-300 rounded-xl focus:ring-orange-400 shadow-sm"
                />
                {currentPage.private && <p className="text-[10px] text-orange-500/70">This is private — only you and your AI Guide see this.</p>}
              </div>
            )}

            {/* CELEBRATION page */}
            {currentPage.type === 'celebration' && (
              <div className="text-center py-8 space-y-6">
                <motion.div initial={{scale:0}} animate={{scale:1}} transition={{type:'spring', damping:10}}>
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-xl shadow-orange-500/30">
                    <Trophy className="w-12 h-12 text-white" />
                  </div>
                </motion.div>
                <div className="space-y-2">
                  {currentPage.content.split('\n').map((line, i) => (
                    <motion.p key={i} initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} transition={{delay: 0.3 + i * 0.15}}
                      className={`${i === 0 ? 'text-2xl font-black text-white' : 'text-white/70 text-sm'}`}>{line}</motion.p>
                  ))}
                </div>
                {currentPage.badge && (
                  <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.8}} className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-amber-500/20 border border-amber-500/30">
                    <Award className="w-6 h-6 text-amber-400" />
                    <span className="text-amber-300 font-bold">{currentPage.badge.name}</span>
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  </motion.div>
                )}
                {currentPage.xp && (
                  <motion.p initial={{opacity:0}} animate={{opacity:1}} transition={{delay:1}} className="text-orange-400 font-bold text-lg">+{currentPage.xp} XP</motion.p>
                )}
              </div>
            )}

            {/* REFLECTION page */}
            {currentPage.type === 'reflection' && (
              <div className="space-y-4">
                <p className="text-gray-800 text-lg font-semibold">{currentPage.content}</p>
                <p className="text-sm text-gray-500">{currentPage.prompt}</p>
                <Textarea
                  value={pageData.reflection || savedData[currentPage.save_keys?.[0]] || ''}
                  onChange={e => { setPageData({...pageData, reflection: e.target.value}); }}
                  onBlur={e => saveResponse(currentPage.id, { [currentPage.save_keys?.[0] || 'reflection']: e.target.value }, currentPage.xp || 0)}
                  placeholder="Be real..."
                  rows={4}
                  className="bg-white border-gray-200 text-gray-800 placeholder:text-gray-300 rounded-xl focus:ring-orange-400 shadow-sm"
                />
                {currentPage.private && <p className="text-[10px] text-orange-500/70">This is private — only you and your AI Guide see this.</p>}
              </div>
            )}

            {/* QUIZ page */}
            {currentPage.type === 'quiz' && (
              <div className="space-y-4">
                {!quizDone ? (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Question {quizCurrent + 1}/{currentPage.questions.length}</p>
                      <div className="flex gap-1">
                        {[0, 1, 2].map(i => (
                          <Heart key={i} className={`w-5 h-5 ${i < quizHearts ? 'text-red-400' : 'text-gray-200'}`} fill={i < quizHearts ? 'currentColor' : 'none'} />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-800 text-lg font-semibold">{currentPage.questions[quizCurrent].prompt}</p>
                    <div className="space-y-2">
                      {currentPage.questions[quizCurrent].options.map((opt, i) => {
                        const answered = pageData[`q${quizCurrent}`];
                        const isCorrect = i === currentPage.questions[quizCurrent].correct;
                        const isSelected = answered?.selected === i;
                        return (
                          <button key={i} onClick={() => !answered && handleQuizAnswer(quizCurrent, i)} disabled={!!answered}
                            className={`w-full p-4 rounded-xl text-left text-sm transition-all ${
                              answered ? (isCorrect ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : isSelected ? 'bg-red-50 border-red-300 text-red-800' : 'bg-white text-gray-400 border-gray-100')
                              : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-100 shadow-sm'
                            } border`}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {pageData[`q${quizCurrent}`] && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-gray-600 italic p-3 rounded-xl bg-gray-50">
                        {pageData[`q${quizCurrent}`].correct ? currentPage.questions[quizCurrent].feedback_correct : currentPage.questions[quizCurrent].feedback_wrong}
                      </motion.p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6 space-y-4">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                      <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                        <Trophy className="w-10 h-10 text-white" />
                      </div>
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900">Badge Earned!</h3>
                    <p className="text-orange-500 font-semibold">{currentPage.badge?.name}</p>
                    <div className="flex justify-center gap-1">
                      {[0, 1, 2].map(i => <Heart key={i} className={`w-6 h-6 ${i < quizHearts ? 'text-red-400' : 'text-gray-200'}`} fill={i < quizHearts ? 'currentColor' : 'none'} />)}
                    </div>
                    <p className="text-sm text-gray-400">{quizHearts === 3 ? 'Gold — Perfect!' : quizHearts === 2 ? 'Silver — Great job!' : 'Bronze — You passed!'}</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom nav */}
      <div className={`px-4 py-3 border-t ${isHookPage || isCelebration ? 'border-white/10 bg-black/50 backdrop-blur-lg' : 'border-gray-200 bg-white/80 backdrop-blur-lg'}`}>
        <div className="max-w-lg mx-auto flex gap-3">
          <Button variant="outline" onClick={goBack} disabled={isFirstPage}
            className={`rounded-xl disabled:opacity-20 ${isHookPage || isCelebration ? 'border-white/10 text-white/60 hover:bg-white/10' : 'border-gray-200 text-gray-500 hover:bg-gray-100'}`}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Button onClick={goNext} className="flex-1 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-md shadow-orange-500/20"
            disabled={currentPage.type === 'quiz' && !quizDone}>
            {isLastPageInModule && isLastModule ? 'Complete Course' : isLastPageInModule ? 'Next Module →' : 'Continue'}
            {!isLastPageInModule && !isLastModule && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
      <Navigation role="youth" />
    </div>
  );
};

export default CoursePage;
